// Adapted from https://github.com/elevenlabs/xi/blob/8b5d2ba6f04ae9091b5400da0c78dcba86464d57/packages/js/player/src/Player.tsx

"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { convertToProject, fetchSettings, getAudioUrl, waitForAudio } from "./api";

import { getClientId, getPlayedDuration, getSessionId, setPlayedDuration } from "./localStorage";
import { useMediaSession } from "./useMediaSession";
import { env } from "@/env";
import { clsx } from "clsx";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";

const IS_PROD_DEPLOYMENT = env.NEXT_PUBLIC_SERVER_URL === "https://elevenlabs.io";
const PREVIEW_MODE = !IS_PROD_DEPLOYMENT;

const PREVIEW_AUDIO_SRC =
  "https://eleven-public-cdn.elevenlabs.io/audio-native/8cb6f91dbb1d1b891d2cde8e21c96378281280fd9dac76faac4c7502f022075a/2SMZeryPo7Oro5ipbpBX/2SMZeryPo7Oro5ipbpBX.mp3";

function computeParams(customAudioSrc?: string) {
  if (customAudioSrc) {
    return {}; // custom audio effect handles loading independently
  }
  if (PREVIEW_MODE) {
    return { preview: true, audioSrc: PREVIEW_AUDIO_SRC };
  }
  return { publicUserId: env.NEXT_PUBLIC_PUBLIC_USER_ID };
}

export function AudioNativePlayer({ audioSrc: customAudioSrc }: { audioSrc?: string } = {}) {
  const tGlobals = useTranslations("ClientKeys.Globals");
  const t = useTranslations("ClientKeys.AudioNative");

  const audioRef = useRef<HTMLAudioElement>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, _setSpeed] = useState(1);
  const [error, setErrorValue] = useState("");
  const [progress, setProgress] = useState<string | null>(null);
  const [initialParams] = useState<any>(() => computeParams(customAudioSrc));
  const playedDuration = useRef(0);
  const [data, setData] = useState<any>({
    // Visual Settings
    title: null,
    image: null,
    author: null,
    // Analytics Settings
    sessionization: 0, // in minutes
    // Audio Native info.
    publicUserId: null,
    projectId: null,
    preview: false,
    audioSrc: null,
    // State info.
    loaded: false,
  });

  // Stable metrics snapshot for `sendEvent` and unload (avoids stale closures without re-running the load effect).
  // Synced in `useLayoutEffect` so we don’t assign refs during render and so it’s ready before the `useEffect` that fires `load`.
  const metricsContextRef = useRef({
    preview: false,
    error: "",
    sessionization: 0,
    publicUserId: null as string | null,
    projectId: null as string | null,
  });
  useLayoutEffect(() => {
    metricsContextRef.current = {
      preview: data.preview,
      error,
      sessionization: data.sessionization,
      publicUserId: data.publicUserId,
      projectId: data.projectId,
    };
  }, [data.preview, error, data.sessionization, data.publicUserId, data.projectId]);

  if (playing && !hasPlayed) {
    setHasPlayed(true);
  }

  const speedOptions = [0.8, 1.0, 1.2, 1.5, 2.0];
  const activeSpeedIndex = speedOptions.indexOf(speed);

  const getParams = useCallback(() => computeParams(customAudioSrc), [customAudioSrc]);

  const pathname = usePathname();
  const pageUrl = `${env.NEXT_PUBLIC_SERVER_URL}${pathname.replace("/abtest/", "/")}`;

  const setError = useCallback(
    (error: string) => {
      setErrorValue(error);
      if (error && !initialParams.qa && !initialParams.preview) {
        logger.error(null, `[AudioNative] ${error}`);
      }
    },
    [initialParams.qa, initialParams.preview],
  );

  const onLoadError = useCallback(
    (error: string) => {
      setError(error);
      setLoading(false);
      if (error) {
        captureEvent(EventName.mw_audio_native_error, { error });
      }
    },
    [setError],
  );

  const sendEvent = useCallback(
    async (eventType: string, fields: { [key: string]: any }, keepalive: boolean = false) => {
      const ctx = metricsContextRef.current;
      if (ctx.preview || ctx.error || !ctx.publicUserId || !ctx.projectId) {
        return;
      }

      const sessionId = getSessionId(ctx.sessionization);
      const url = `${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/audio-native/${ctx.publicUserId}/${ctx.projectId}/metrics`;

      const body = {
        session_id: sessionId,
        event_type: eventType,
        fields,
      };

      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        keepalive,
      }).catch(() => {});
    },
    [],
  );

  const onProjectLoaded = useCallback(
    async (params: any, audioIsOrWillBeAvailable: boolean, audioNativeMode: boolean) => {
      if (!audioIsOrWillBeAvailable) {
        // Don't fetch audio if it is not and won't be available.
        onLoadError("Auto-convert is disabled for this url.");
        return;
      }

      const waitForAudioRequest = (url: string) =>
        waitForAudio(url, audioNativeMode ? 11 : 1, () => setProgress("Converting the page to audio"));

      const settingsRequest = fetchSettings(params.publicUserId, params.projectId);

      const onErrorWrapper = (error: Error) => {
        if (audioNativeMode) {
          onLoadError(error.message);
        } else {
          onLoadError("Audio not found for this project.");
        }
      };

      settingsRequest
        .then(([settings, status]) => {
          if (status !== 200) {
            onLoadError(settings.detail.message);
          } else {
            const url = settings.audio_url
              ? settings.audio_url
              : getAudioUrl(params.publicUserId, params.projectId, settings.audio_path); // To remove once audio_url is available in Xi in production

            waitForAudioRequest(url)
              .then(audioSrc => {
                params.audioSrc = audioSrc;
                setData({
                  ...params,
                  ...settings,
                  loaded: true,
                });
                onLoadError("");
                playedDuration.current = getPlayedDuration(params.sessionization);
              })
              .catch(onErrorWrapper);
          }
        })
        .catch(onErrorWrapper);
    },
    [onLoadError],
  );

  useEffect(() => {
    if (!data.loaded) {
      return;
    }
    onLoadError("");
    sendEvent("load", {
      user_agent: navigator.userAgent,
      current_time: 0,
      client_id: getClientId(),
    });

    let unloaded = false;

    function onUnload() {
      if (unloaded || playedDuration.current === 0) return;
      unloaded = true;

      const { sessionization } = metricsContextRef.current;
      setPlayedDuration(sessionization, playedDuration.current);
      sendEvent("player_duration", { player_duration: playedDuration.current }, true);
      if (audioRef.current?.duration) {
        sendEvent(
          "completion_rate",
          {
            completion_rate: playedDuration.current / audioRef.current.duration,
          },
          true,
        );
      }
      if (audioRef.current) {
        sendEvent("unload", { current_time: audioRef.current.currentTime }, true);
      }
      captureEvent(EventName.mw_audio_native_unload, {
        playedDuration: playedDuration.current,
        completionRate: audioRef.current?.duration ? playedDuration.current / audioRef.current?.duration : undefined,
      });
    }

    function onVisibilityChange() {
      if (document.visibilityState === "hidden") {
        onUnload();
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      onUnload();
    };
  }, [data.loaded, sendEvent, onLoadError]);

  // custom audio scenario: direct URL provided via prop, bypasses convert API
  useEffect(() => {
    if (!customAudioSrc) return;
    setData((prev: any) => ({ ...prev, audioSrc: customAudioSrc, loaded: true }));
  }, [customAudioSrc]);

  // preview scenario
  useEffect(() => {
    if (loading) return;

    const params = { ...data, ...getParams() };

    // Run only for preview scenario.
    if (!params.preview) return;

    setData(params);

    if (params.publicUserId && params.projectId) {
      setLoading(true);
      onProjectLoaded(params, true, false);
      return;
    }

    if (params.audioSrc === null) {
      setError("No audio source provided.");
      setLoading(false);
      return;
    }

    setData({ ...params, loaded: true });
    // Intentionally omit `data`/`loading`/`setError`: listing them retriggers during conversion and can double-fetch; `onProjectLoaded` stays current via `useCallback`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onProjectLoaded, getParams]);

  // audio native scenario. We need to fetch projectId for the page url.
  useEffect(() => {
    if (loading) return;
    const params = { ...data, ...getParams() };

    // Run only for audio native scenario.
    if (params.preview || params.projectId || customAudioSrc) return;

    setData(params);

    if (params.audioSrc !== null) {
      setError("Cannot provide direct audio source in production mode.");
      setLoading(false);
      return;
    }
    if (params.publicUserId === null) {
      setError("Public User ID must be provided in the production mode.");
      setLoading(false);
      return;
    }

    // Show loading progress only after 1 second.
    const timeoutId = setTimeout(() => setProgress("Converting the page to audio"), 1000);
    const cleanup = () => clearTimeout(timeoutId);

    // Wait for pageUrl to load
    if (!pageUrl) return cleanup;

    async function load() {
      setLoading(true);
      try {
        const [projectId, audioIsOrWillBeAvailable] = await convertToProject(params.publicUserId, pageUrl!);
        clearTimeout(timeoutId);
        params.projectId = projectId;
        onProjectLoaded(params, audioIsOrWillBeAvailable, true);
      } catch (e) {
        onLoadError((e as Error).message);
      }
    }
    load();
    return cleanup;
    // Intentionally omit `data`/`loading`/`setError`: same as preview effect — avoids retry storms while handlers stay fresh via `useCallback`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageUrl, onLoadError, onProjectLoaded, customAudioSrc, getParams]);

  const formatTime = (time: number, duration?: number) => {
    const oneHour = 3600;
    const tenMins = 600;
    const [t, n] = time >= oneHour ? [11, 8] : typeof duration === "number" && duration < tenMins ? [15, 4] : [14, 5];
    return new Date(1e3 * time).toISOString().substr(t, n);
  };

  const seekBySeconds = useCallback(
    (amount: number) => {
      if (!audioRef.current) {
        return;
      }

      const currTime = audioRef.current.currentTime;
      audioRef.current.currentTime = Math.min(
        audioRef.current.duration,
        Math.max(0, audioRef.current.currentTime + amount),
      );
      if (!playing) {
        audioRef.current.pause();
      }
      sendEvent(amount < 0 ? "skip_bwd" : "skip_fwd", { current_time: currTime });
      captureEvent(EventName.mw_audio_native_skip, {
        direction: amount < 0 ? "backward" : "forward",
        currentTime: currTime,
      });
    },
    [audioRef, playing, sendEvent],
  );

  const seekTo = useCallback(
    (seekTime: number) => {
      if (!audioRef.current) {
        return;
      }

      const currTime = audioRef.current.currentTime;
      audioRef.current.currentTime = seekTime;
      if (!playing) {
        audioRef.current.pause();
      }
      sendEvent(seekTime < currTime ? "seek_bwd" : "seek_fwd", {
        current_time: currTime,
      });
      captureEvent(EventName.mw_audio_native_seek, {
        direction: seekTime < currTime ? "backward" : "forward",
        currentTime: currTime,
      });
    },
    [playing, sendEvent],
  );

  const play = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play();
      setPlaying(true);
      sendEvent("play", {
        user_agent: navigator.userAgent,
        current_time: audioRef.current.currentTime,
        client_id: getClientId(),
      });
      captureEvent(EventName.mw_audio_native_play, { currentTime: audioRef.current.currentTime });
    }
  }, [sendEvent]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
      sendEvent("pause", { current_time: audioRef.current.currentTime });
      captureEvent(EventName.mw_audio_native_pause);
    }
  }, [sendEvent]);

  const playPause = () => {
    if (playing) {
      pause();
    } else {
      play();
    }
  };

  const setSpeed = (val: number) => {
    if (audioRef.current) {
      // let e = 2 === speed ? 1 : speed + 0.25;
      _setSpeed(val);
      audioRef.current.playbackRate = val;
      sendEvent("toggle_speed", { current_time: audioRef.current.currentTime });
      captureEvent(EventName.mw_audio_native_speed_change, { speed: val });
    }
  };

  useMediaSession({
    title: data.title,
    author: data.author,
    loaded: data.loaded,
    actions: {
      play,
      pause,
      seekTo,
      seekBySeconds,
    },
  });

  const isHidden = !data.loaded || !duration || !!error;

  return (
    <div inert={isHidden} className={clsx("tw-transition-opacity", isHidden && "tw-opacity-0")}>
      <audio
        autoPlay={false}
        ref={audioRef}
        src={data.audioSrc}
        preload='metadata'
        onPlay={() => {
          setPlaying(true);
        }}
        onPause={() => {
          setPlaying(false);
        }}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
        onProgress={ev => {
          if (ev.currentTarget.seekable.length > 0) {
            setDuration(ev.currentTarget.seekable.end(ev.currentTarget.seekable.length - 1));
          }
        }}
        onDurationChange={ev => {
          const d = ev.currentTarget.duration;
          if (Number.isFinite(d)) {
            setDuration(d);
          }
        }}
        onTimeUpdate={ev => {
          const delta = Math.abs(ev.currentTarget.currentTime - currentTime);
          if (delta < 0.75) {
            playedDuration.current += delta;
          }
          setCurrentTime(ev.currentTarget.currentTime);
        }}
      />
      <div className='tw-flex tw-items-center tw-gap-3'>
        <button
          type='button'
          className='tw-flex-none tw-outline-none focus-visible:tw-outline-focus tw-rounded-full tw-size-10 tw-bg-white tw-flex tw-items-center tw-justify-center'
          aria-label={playing ? tGlobals("pause") : tGlobals("play")}
          onClick={() => {
            playPause();
          }}
          style={{ boxShadow: "0 0 0 1px #0000000f, 0 1px 2px 0 #0000000a, 0 2px 4px 0 #0000000a" }}
        >
          {playing ? (
            <svg viewBox='0 0 24 24' aria-hidden className='tw-size-4'>
              <path
                fill='#000'
                d='M6.75 3A2.75 2.75 0 0 0 4 5.75v12.5A2.75 2.75 0 0 0 6.75 21h.5A2.75 2.75 0 0 0 10 18.25V5.75A2.75 2.75 0 0 0 7.25 3zM16.75 3A2.75 2.75 0 0 0 14 5.75v12.5A2.75 2.75 0 0 0 16.75 21h.5A2.75 2.75 0 0 0 20 18.25V5.75A2.75 2.75 0 0 0 17.25 3z'
              />
            </svg>
          ) : (
            <svg viewBox='0 0 24 24' aria-hidden className='tw-size-4'>
              <path
                fill='#000'
                d='M9.244 2.368C7.414 1.184 5 2.497 5 4.676v14.648c0 2.18 2.414 3.493 4.244 2.309l11.318-7.324c1.675-1.084 1.675-3.534 0-4.618z'
              />
            </svg>
          )}
        </button>
        <div className='tw-grid tw-grid-cols-1 tw-grid-rows-1 tw-items-center'>
          <div
            inert={hasPlayed}
            className={clsx(
              "tw-col-start-1 tw-row-start-1 tw-transition-opacity",
              hasPlayed ? "tw-opacity-0" : "tw-delay-150",
            )}
          >
            <p className='tw-type-sm tw-font-medium tw-text-black'>
              <span className='tw-inline sm:tw-hidden'>{t("listen_short")}</span>
              <span className='tw-hidden sm:tw-inline'>{t("listen")}</span>
            </p>
            <time
              dateTime={formatTime(duration)}
              className='tw-mt-2.5 tw-block tw-type-xs tw-font-medium tw-text-gray-500'
            >
              {formatTime(duration, duration)}
              {PREVIEW_MODE && !customAudioSrc ? " [PREVIEW]" : ""}
            </time>
          </div>
          <div
            inert={!hasPlayed}
            className={clsx(
              "tw-col-start-1 tw-row-start-1 tw-flex tw-items-center tw-transition-opacity",
              hasPlayed ? "tw-delay-150" : "tw-opacity-0",
            )}
          >
            <time
              className='tw-type-sm tw-font-medium tw-text-black tw-tabular-nums tw-hidden sm:tw-block'
              dateTime={formatTime(currentTime)}
            >
              {formatTime(currentTime, duration)}
            </time>
            <time
              className='tw-ml-1.5 tw-type-sm tw-font-medium tw-text-black tw-relative tw-pl-3 tw-tabular-nums tw-hidden sm:tw-block'
              dateTime={formatTime(duration)}
            >
              <svg
                viewBox='0 0 5 12'
                fill='none'
                aria-hidden
                className='tw-h-3 tw-absolute tw-left-0 tw-top-0 tw-text-gray-300'
              >
                <path stroke='currentColor' strokeLinecap='round' strokeWidth='1.5' d='M1 11 4 1' />
              </svg>
              {formatTime(duration, duration)}
            </time>
            <button
              type='button'
              className='sm:tw-ml-3 tw-size-8 tw-flex tw-items-center tw-justify-center tw-text-gray-500 hover:tw-text-black tw-outline-none focus-visible:tw-outline-focus tw-transition tw-rounded'
              aria-label={t("rewind", { seconds: 10 })}
              onClick={() => seekBySeconds(-10)}
            >
              <svg fill='none' viewBox='0 0 24 24' aria-hidden className='tw-size-5'>
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='1.5'
                  d='M4.35 9.25c.95-3.25 4.191-5.5 7.655-5.5 3.495 0 6.482 2.28 7.687 5.5M3.75 4.75v4.5h4.5'
                />
                <path
                  fill='currentColor'
                  d='M7.664 20.156v-6.17h-.038l-.87.616c-.273.177-.431.234-.615.234-.35 0-.61-.26-.61-.609 0-.267.165-.501.489-.717l1.136-.794c.457-.31.82-.438 1.175-.438.615 0 1.028.426 1.028 1.067v6.81c0 .585-.311.921-.85.921-.534 0-.845-.343-.845-.92M10.857 16.88v-.406c0-2.602 1.244-4.304 3.307-4.304 2.07 0 3.288 1.682 3.288 4.304v.406c0 2.597-1.257 4.304-3.313 4.304s-3.282-1.688-3.282-4.304m1.714-.4v.388c0 1.828.597 2.92 1.58 2.92s1.58-1.092 1.58-2.92v-.387c0-1.822-.596-2.914-1.58-2.914-.983 0-1.58 1.085-1.58 2.913'
                />
              </svg>
            </button>
            <button
              type='button'
              className='tw-size-8 tw-flex tw-items-center tw-justify-center tw-text-gray-500 hover:tw-text-black tw-outline-none focus-visible:tw-outline-focus tw-transition tw-rounded'
              aria-label={t("fast_forward", { seconds: 10 })}
              onClick={() => seekBySeconds(10)}
            >
              <svg fill='none' viewBox='0 0 24 24' aria-hidden className='tw-size-5'>
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='1.5'
                  d='M19.65 9.25c-.95-3.25-4.19-5.5-7.655-5.5-3.495 0-6.482 2.28-7.686 5.5m15.34 0h.601v-4.5m-.6 4.5h-3.9'
                />
                <path
                  fill='currentColor'
                  d='M8.164 20.156v-6.17h-.038l-.87.616c-.273.177-.431.234-.615.234-.35 0-.61-.26-.61-.609 0-.267.165-.501.489-.717l1.136-.794c.457-.31.82-.438 1.175-.438.615 0 1.028.426 1.028 1.067v6.81c0 .585-.311.921-.85.921-.534 0-.845-.343-.845-.92M11.357 16.88v-.406c0-2.602 1.244-4.304 3.307-4.304 2.07 0 3.288 1.682 3.288 4.304v.406c0 2.597-1.257 4.304-3.313 4.304s-3.282-1.688-3.282-4.304m1.714-.4v.388c0 1.828.597 2.92 1.58 2.92s1.58-1.092 1.58-2.92v-.387c0-1.822-.596-2.914-1.58-2.914-.983 0-1.58 1.085-1.58 2.913'
                />
              </svg>
            </button>
            <button
              type='button'
              aria-label={t("playback_speed", { speed: `${speed.toFixed(1)}x` })}
              className='tw-h-8 tw-px-2.5 tw-flex tw-items-center tw-justify-center tw-type-sm tw-font-medium tw-text-gray-500 tw-tabular-nums hover:tw-text-black tw-outline-none focus-visible:tw-outline-focus tw-transition tw-rounded'
              onClick={() => {
                if (speedOptions[activeSpeedIndex + 1]) {
                  setSpeed(speedOptions[activeSpeedIndex + 1]);
                } else {
                  setSpeed(speedOptions[0]);
                }
              }}
              onKeyDown={e => {
                if (e.key === "ArrowRight" && speedOptions[activeSpeedIndex + 1]) {
                  setSpeed(speedOptions[activeSpeedIndex + 1]);
                } else if (e.key === "ArrowLeft" && speedOptions[activeSpeedIndex - 1]) {
                  setSpeed(speedOptions[activeSpeedIndex - 1]);
                }
              }}
            >
              {`${speed.toFixed(1)}x`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
